import React, { createContext, useContext, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser, clearUser, updateUser as updateUserAction, setSubscribed } from "../state/slices/userSlice";
import { clearVendorAuth } from "../state/slices/vendorSlice";

const AuthContext = createContext();

const checkSubscriptionStatus = (userId) =>
    fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/status?user_id=${userId}`)
        .then((res) => res.json())
        .then((data) => ({
            isSubscribed: data.is_subscribed || false,
            subscriptionStartDate: data.subscription_start_date || null,
        }))
        .catch(() => ({ isSubscribed: false, subscriptionStartDate: null }));

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const token = useSelector((state) => state.user.token);
    const isSubscribed = useSelector((state) => state.user.isSubscribed);
    const status = useSelector((state) => state.user.status);
    const loading = status === "loading";

    // Check subscription whenever user id changes (covers login, page reload, register)
    useEffect(() => {
        if (user?.id) {
            checkSubscriptionStatus(user.id).then((s) => dispatch(setSubscribed(s)));
        }
    }, [user?.id, dispatch]);

    // Listen for vendor-login event → clear user session
    useEffect(() => {
        const handleUserLogout = () => dispatch(clearUser());
        window.addEventListener("user-logout", handleUserLogout);
        return () => window.removeEventListener("user-logout", handleUserLogout);
    }, [dispatch]);

    const login = useCallback((userData, jwtToken) => {
        dispatch(clearVendorAuth());
        window.dispatchEvent(new Event("vendor-logout"));
        dispatch(setUser({ user: userData, token: jwtToken }));
    }, [dispatch]);

    const logout = useCallback(() => {
        dispatch(clearUser());
    }, [dispatch]);

    const updateUser = useCallback((userData) => {
        dispatch(updateUserAction(userData));
    }, [dispatch]);

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                loading,
                isSubscribed,
                login,
                logout,
                updateUser,
                isAuthenticated: !!token,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
